import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import Button from "@/shared/components/Button";
import Input from "@/shared/components/Input";
import { buildBackendUrl } from "@/shared/api/backend";
import backgroundImage from "@/pages/authentication/assets/background-1.webp";
import icon from "@/pages/authentication/assets/icon.svg";
import iconLock from "@/pages/authentication/assets/icon-lock.svg";
import iconWarning from "@/pages/authentication/assets/icon-warning.svg";

type AccessState = "checking" | "locked" | "unlocked";

export default function ExternalAccessGate({ children }: { children: ReactNode }) {
    const [accessState, setAccessState] = useState<AccessState>("checking");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        void fetch(buildBackendUrl("/public/access"), {
            cache: "no-store",
            credentials: "include",
            signal: controller.signal,
        }).then((response) => {
            setAccessState(response.ok ? "unlocked" : "locked");
            if (!response.ok && response.status !== 401) {
                setError("Unable to check external access. Try again.");
            }
        }).catch((reason: unknown) => {
            if (!(reason instanceof DOMException && reason.name === "AbortError")) {
                setAccessState("locked");
                setError("Unable to check external access. Try again.");
            }
        });
        return () => controller.abort();
    }, []);

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const response = await fetch(buildBackendUrl("/public/access"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
                cache: "no-store",
                credentials: "include",
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({})) as { error?: string };
                setError(payload.error || "Incorrect password.");
                return;
            }
            setPassword("");
            setAccessState("unlocked");
        } catch {
            setError("Unable to verify the password. Try again.");
        } finally {
            setSubmitting(false);
        }
    }

    if (accessState === "unlocked") {
        return <>{children}</>;
    }

    return (
        <main className="relative grid min-h-dvh place-items-center overflow-hidden bg-slate-100 px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] text-slate-900 sm:px-6 sm:py-8">
            <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
                <img src={backgroundImage} alt="" className="h-full w-full object-cover opacity-20 blur-[8px]" />
                <div className="absolute inset-0 bg-white/55" />
            </div>

            <div className="relative w-full max-w-[50rem]">
                <div className="mb-4 flex justify-center md:hidden">
                    <img src={icon} alt="HOH - Bed Board" className="h-12 w-12" />
                </div>
                <section className="grid w-full overflow-hidden rounded-2xl shadow-md md:min-h-[25rem] md:grid-cols-10">
                    <div className="hidden flex-col bg-[#005FB8] p-5 text-white md:col-span-4 md:flex">
                        <div className="mb-5 flex items-center gap-3">
                            <img src={icon} alt="" className="h-8 w-8" />
                            <p className="text-[18px] font-bold">HOH - Bed Board</p>
                        </div>
                        <div className="mt-4 rounded-xl bg-white/10 p-4">
                            <p className="text-sm leading-6 text-white/85">
                                Secure access to hospital external pages.
                            </p>
                        </div>
                        <div className="grow" />
                        <div className="flex h-10 items-center rounded-md bg-[#196fbf]">
                            <img className="h-full p-3" src={iconLock} alt="" />
                            <span className="text-xs">Authorized access only</span>
                        </div>
                    </div>

                    <div className="flex flex-col bg-white p-5 text-black sm:p-7 md:col-span-6 md:p-12">
                        <h1 className="text-2xl font-bold text-[#003464] md:text-[28px]">External access</h1>
                        <p className="mb-4 text-slate-500">Enter the shared password to continue.</p>

                        {accessState === "checking" ? (
                            <div className="flex min-h-32 items-center gap-3 text-sm text-slate-500" role="status">
                                <span aria-hidden="true" className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-[#005fb8]" />
                                Checking access...
                            </div>
                        ) : (
                            <form className="flex flex-col gap-3 py-4" onSubmit={submit}>
                                <label className="text-sm font-semibold text-slate-700" htmlFor="external-access-password">Password</label>
                                <Input
                                    id="external-access-password"
                                    className="h-11 text-base sm:text-sm"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    required
                                    autoFocus
                                    onChange={(event) => setPassword(event.target.value)}
                                />
                                {error ? <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}
                                <Button type="submit" variant="primary" className="h-11 mt-2" disabled={submitting || !password}>
                                    {submitting ? "Checking..." : "Continue"} <span aria-hidden="true">&#8594;</span>
                                </Button>
                            </form>
                        )}

                        <div className="mt-auto">
                            <div className="my-4 flex items-center"><hr className="grow border-t border-gray-300" /></div>
                            <div className="flex items-center justify-center">
                                <img src={iconWarning} alt="" className="mr-2 h-4 w-4" />
                                <p className="text-xs font-bold uppercase tracking-wider text-[#DC2626]">Authorized Personnel Only</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
}
